import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { config } from '../../../../config/config';
import { TypedMemo } from '../../../common/types';
import styles from './NativeUpgradeNote.module.scss';

/**
 * Where a native app's own Subscription pane lives. Both apps route `<scheme>://settings?tab=…` to
 * that Settings pane (music24-ios `DeepLink.parse`, music24-android `AppDeepLink`), and both web
 * views hand a link in the app's own scheme to that router instead of loading it.
 */
export function nativeSubscriptionLink(): string {
  return `${config.nativeAppScheme}://settings?tab=subscription`;
}

/** The `<link>` tag of the message: the app's Subscription pane. */
function renderSubscriptionLink(chunks: React.ReactNode[]): React.ReactNode {
  return (
    <a key="subscription" href={nativeSubscriptionLink()} className={styles.link}>
      {chunks}
    </a>
  );
}

interface NativeUpgradeNoteProps {
  className?: string;
}

/**
 * Shown in place of every checkout button when the page is drawn inside a native app's web view.
 * App Store guideline 3.1.1 and Play billing let the app sell a plan only through its own
 * in-app purchase, so the web view never starts a Stripe checkout and never links to a web price.
 * It sends the reader to the app's Subscription pane instead.
 */
const NativeUpgradeNoteComponent: React.FC<NativeUpgradeNoteProps> = ({ className }) => {
  const intl = useIntl();

  return (
    <p className={clsx(styles.note, className)}>
      {intl.formatMessage(
        {
          id: 'app.sml.upgrade.nativeEmbed.upgradeInApp',
          defaultMessage: 'Upgrade in the app: <link>Settings → Subscription</link>',
        },
        { link: renderSubscriptionLink }
      )}
    </p>
  );
};

export const NativeUpgradeNote = TypedMemo(NativeUpgradeNoteComponent);
